import { Model } from 'higgins-domain'

import { configuratorInterface } from './configurator-interface.js'

export const onBaseCabinetDeleted = configuratorInterface.transformerBuilder.when
  .entity(Model.BaseCabinet.types)
  .deleted()
  .then.applySideEffect(ctx => {
    const deletedOffsetX = ctx.getOld$Entity().offsetX
    const countertop = ctx.getOldState().entities.find(e => e.type === 'Countertop' && e.offsetX === deletedOffsetX)

    return countertop
      ? [
          configuratorInterface.updates.EntityDeletion({
            entityId: countertop.id,
            updateTag: 'deleteRelatedCountertop',
          }),
        ]
      : []
  })
  .build('OnBaseCabinetDeleted')

export const selectBaseCabinetOnCountertopSelected = configuratorInterface.transformerBuilder.when
  .entity([Model.Countertop.type])
  .selected()
  .then.select.entity(ctx => {
    // Due to a bug in getSelected$Entity, we need to make sure that we aren't adding an entity here (i.e. the user clicked on a fab)
    if (ctx.updates.length === 1) {
      const selectedOffsetX = ctx.getSelected$Entity().offsetX

      const baseCabinet = ctx
        .getOld$Root()
        .$segments.find(e => Model.BaseCabinet.is(e) && e.offsetX === selectedOffsetX)

      return baseCabinet?.id ?? ctx.getSelected$Entity().id
    }

    return null
  })
  .build('SelectBaseCabinetOnCountertopSelected')

export const slideSegmentsOnDeletion = configuratorInterface.transformerBuilder.when
  .entity(Model.Segment.types)
  .deleted()
  .and(ctx => {
    const deleted = ctx.getOld$Entity()
    if (Model.BaseCabinet.is(deleted)) {
      // If it's a base cabinet, we don't slide if there is still a wall cabinet at the same offset (we need to ignore countertops)
      return !ctx.getOld$Root().$segments.some(s => Model.WallCabinet.is(s) && s.offsetX === deleted.offsetX)
    } else {
      // Otherwise we don't slide if there is any other segment at the same offset
      return !ctx.getOld$Root().$segments.some(s => s.id !== deleted.id && s.offsetX === deleted.offsetX)
    }
  })
  .then.applySideEffect(ctx => {
    const modifications = []

    const deletedEntity = ctx.getOld$Entity()
    for (const e of ctx.getOld$Root().$segments) {
      if (e.id !== deletedEntity.id && e.offsetX > deletedEntity.offsetX) {
        modifications.push(
          configuratorInterface.updates.DomainEntityModification({
            entityId: e.id,
            stateDelta: { offsetX: e.offsetX - deletedEntity.$width },
            updateTag: 'slideSegments',
          })
        )
      }
    }

    return [...modifications]
  })
  .build('SlideSegmentsOnDeletion')

export const deleteMatchingCountertop = configuratorInterface.transformerBuilder.when
  .entity(Model.BaseCabinet.types)
  .replacedWith([...Model.TallCabinet.types, ...Model.NoCabinet.types])
  .then.applySideEffect(ctx => {
    const oldCountertop = ctx.getReplaced$Entity().$matchingCountertop
    if (oldCountertop) {
      return [
        configuratorInterface.updates.EntityDeletion({
          entityId: oldCountertop.id,
          updateTag: 'deleteRelatedCountertop',
        }),
      ]
    } else {
      return []
    }
  })
  .build('DeleteMatchingCountertop')

export const createMatchingCountertop = configuratorInterface.transformerBuilder.when
  .entity([...Model.TallCabinet.types, ...Model.NoCabinet.types])
  .replacedWith(Model.BaseCabinet.types)
  .then.applySideEffect(ctx => {
    return [
      configuratorInterface.updates.DomainEntityCreation({
        entity: {
          id: ctx.getIdSequence().nextId('Countertop'),
          type: 'Countertop',
          offsetX: ctx.getReplaced$Entity().offsetX,
        },
        updateTag: 'createMatchingCountertop',
        parentId: ctx.getReplacing$Entity().id,
      }),
    ]
  })
  .build('CreateMatchingCountertop')

export const slideSegmentsOnSegment24To36 = configuratorInterface.transformerBuilder.when
  .entity(Model.Segment24.types)
  .replacedWith(Model.Segment36.types)
  .then.applySideEffect(ctx => {
    const modifications = []

    const replacedEntity = ctx.getReplaced$Entity()

    const widthIncrease = 12

    // Find all segments to the right of the replaced segment and slide them right
    for (const segment of ctx.getOld$Root().$segments) {
      if (segment.id !== replacedEntity.id && segment.offsetX > replacedEntity.offsetX) {
        modifications.push(
          configuratorInterface.updates.DomainEntityModification({
            entityId: segment.id,
            stateDelta: { offsetX: segment.offsetX + widthIncrease },
            updateTag: 'slideSegmentsOnSegment24To36',
          })
        )
      }
    }

    return modifications
  })
  .build('SlideSegmentsOnSegment24To36')

export const slideSegmentsOnSegment36To24 = configuratorInterface.transformerBuilder.when
  .entity(Model.Segment36.types)
  .replacedWith(Model.Segment24.types)
  .then.applySideEffect(ctx => {
    const modifications = []

    const replacedEntity = ctx.getReplaced$Entity()

    const widthDecrease = -12

    // Find all segments to the right of the replaced segment and slide them left
    for (const segment of ctx.getOld$Root().$segments) {
      if (segment.id !== replacedEntity.id && segment.offsetX > replacedEntity.offsetX) {
        modifications.push(
          configuratorInterface.updates.DomainEntityModification({
            entityId: segment.id,
            stateDelta: { offsetX: segment.offsetX + widthDecrease },
            updateTag: 'slideSegmentsOnSegment36To24',
          })
        )
      }
    }

    return modifications
  })
  .build('SlideSegmentsOnSegment36To24')
